import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Box,
  CssBaseline,
  Divider,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import AssignmentIcon from '@mui/icons-material/Assignment';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import ScheduleIcon from '@mui/icons-material/Schedule';
import PeopleIcon from '@mui/icons-material/People';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ListAltIcon from '@mui/icons-material/ListAlt';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import LockResetIcon from '@mui/icons-material/LockReset';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import LogoutIcon from '@mui/icons-material/Logout';
import styled from 'styled-components';

import userProfileImage from '../assets/images/mvlogo.png'; // Make sure this path is correct!

const drawerWidth = 240;

const allDrawerItems = [
  { text: 'Home', icon: <HomeIcon />, path: '/dashboard' },
  { text: 'My Task', icon: <AssignmentIcon />, path: '/dashboard/my-task' },
  {
    text: 'Form Management',
    icon: <FolderOpenIcon />,
    isAdminOnly: true,
    children: [
      { text: 'Upload Form', icon: <UploadFileIcon />, path: '/dashboard/form-management/upload' },
      { text: 'List/Edit/Search Template Forms', icon: <ListAltIcon />, path: '/dashboard/form-management/list' },
      { text: 'List/View/Search Submitted Forms', icon: <ListAltIcon/>, path:'/dashboard/form-management/list-saved-forms'}
    ],
  },
  { text: 'Schedule Task', icon: <ScheduleIcon />, path: '/dashboard/schedule-task', isAdminOnly: true },
  {
    text: 'User Management',
    icon: <PeopleIcon />,
    children: [
      { text: 'Create User', icon: <PersonAddIcon />, path: '/dashboard/user-management/create', isAdminOnly: true },
      { text: 'Reset Password', icon: <LockResetIcon />, path: '/dashboard/user-management/forgot-password', isAdminOnly: false },
    ],
  },
  { text: 'Logout', icon: <LogoutIcon />, action: 'logout' },
];

const CircularImage = styled.img`
  width: 80px;
  height: 80px;
  border-radius: 21%;
  object-fit: cover;
  border: 2px solid #fff;
`;

const ProfileImageContainer = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 16px;
  background-color: white;
  margin-bottom: 8px;
`;


const DashboardLayout = () => {
  const [openDrawer, setOpenDrawer] = useState(false);
  const [openCollapse, setOpenCollapse] = useState({});
  const [userInfo, setUserInfo] = useState({
    name: 'Guest',
    email: 'N/A',
    designation: 'N/A',
    role: 'guest', // Default role for safety. Will be updated from localStorage.
  });
  const navigate = useNavigate();
  const location = useLocation();

  const [filteredDrawerItems, setFilteredDrawerItems] = useState([]);

  useEffect(() => {
    const storedUserString = localStorage.getItem('user');
    // --- ADD THIS CONSOLE LOG ---
    console.log("1. User data from localStorage:", storedUserString);

    if (storedUserString) {
        try {
            const user = JSON.parse(storedUserString);
            if (user && user.token) {
                setUserInfo({
                    name: user.name || 'User',
                    email: user.email || 'N/A',
                    designation: user.designation || 'N/A',
                    role: user.designation, // IMPORTANT: Use 'designation' as the 'role'
                });
                // --- ADD THIS CONSOLE LOG ---
                console.log("2. Parsed user object:", user);
                console.log("3. userInfo.role set to:", user.designation);
            } else {
                console.error("User data in localStorage is incomplete (missing token).");
                localStorage.removeItem('user');
                navigate('/login', { replace: true });
            }
        } catch (e) {
            console.error("Failed to parse user data from localStorage", e);
            localStorage.removeItem('user');
            navigate('/login', { replace: true });
        }
    } else {
        console.log("No user data found in localStorage. Redirecting to login.");
        navigate('/login', { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    console.log("4. Filtering drawer items. Current userInfo.role:", userInfo.role);

    const newFilteredItems = allDrawerItems.map(item => { // Use map instead of filter for top-level to allow modification of children
      // 1. Handle top-level items with isAdminOnly flag
      if (item.isAdminOnly) {
        const isVisible = userInfo.role === 'Admin';
        console.log(`   - Top-level item: "${item.text}", isAdminOnly: true, user role: "${userInfo.role}", Visible: ${isVisible}`);
        return isVisible ? item : null; // Return null if not visible, we'll filter nulls later
      }

      // 2. Handle items with children (like 'User Management')
      if (item.children) {
        const filteredChildren = item.children.filter(child => {
          let isChildVisible;
          if (child.isAdminOnly === false) {
            isChildVisible = true;
          } else {
            isChildVisible = userInfo.role === 'Admin';
          }
          console.log(`     - Child item: "${child.text}", isAdminOnly: ${child.isAdminOnly}, user role: "${userInfo.role}", Visible: ${isChildVisible}`);
          return isChildVisible;
        });

        if (filteredChildren.length > 0) {
            console.log(`   - Parent item: "${item.text}" has visible children. Keeping parent.`);
            // Return a NEW object for the parent with its filtered children
            return { ...item, children: filteredChildren };
        }
        console.log(`   - Parent item: "${item.text}" has NO visible children. Hiding parent.`);
        return null; // Hide parent if no children are visible to this user
      }

      // 3. Default case: Item is visible to all if no isAdminOnly or children logic applies
      console.log(`   - Default visible item: "${item.text}"`);
      return item;
    }).filter(item => item !== null); // Filter out any nulls from the map operation

    setFilteredDrawerItems(newFilteredItems);
  }, [userInfo.role]);


  const handleDrawerToggle = () => {
    setOpenDrawer(!openDrawer);
  };

  const handleCollapseClick = (itemText) => {
    setOpenCollapse((prev) => ({ ...prev, [itemText]: !prev[itemText] }));
  };

  const handleMenuItemClick = (path) => {
    navigate(path);
    setOpenDrawer(false);
  };

  const handleLogout = () => {
    alert('Logged out!');
    localStorage.removeItem('user');
    navigate('/login', { replace: true });
    setOpenDrawer(false);
  };

  const DrawerContent = (
    <>
      <Toolbar />
      <ProfileImageContainer>
        <CircularImage src={userProfileImage} alt="User Profile" />
      </ProfileImageContainer>
      <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.3)' }} />

      <List>
        {filteredDrawerItems.map((item, index) => (
          <React.Fragment key={item.text}>
            {item.action === 'logout' && (
              <Divider sx={{ my: 1, borderColor: 'rgba(255, 255, 255, 0.3)' }} />
            )}

            {item.children ? (
              <>
                <ListItemButton onClick={() => handleCollapseClick(item.text)}>
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.text} />
                  {openCollapse[item.text] ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
                <Collapse in={openCollapse[item.text]} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {item.children.map((child, childIndex) => (
                      <ListItem key={child.text} disablePadding>
                        <ListItemButton
                          sx={{ pl: 4 }}
                          selected={location.pathname === child.path}
                          onClick={() => handleMenuItemClick(child.path)}
                        >
                          <ListItemIcon>{child.icon}</ListItemIcon>
                          <ListItemText primary={child.text} />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              </>
            ) : (
              <ListItem disablePadding>
                <ListItemButton
                  onClick={item.action === 'logout' ? handleLogout : () => handleMenuItemClick(item.path)}
                  selected={item.path ? location.pathname === item.path : false}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItemButton>
              </ListItem>
            )}
          </React.Fragment>
        ))}
      </List>
    </>
  );

  return (
    <Box sx={{ display: 'flex', height: '100vh', width: '100vw', overflow: 'hidden' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          background: 'linear-gradient(to right, #4A148C, #1A237E, #0D47A1)',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Smart Forms
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', mr: 2 }}>
            <Typography variant="subtitle1" noWrap>
              Hello, {userInfo.name}!
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>

      <Drawer
        variant="temporary"
        open={openDrawer}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: {
            width: drawerWidth,
            boxSizing: 'border-box',
            background: 'linear-gradient(to right, #4A148C, #1A237E, #0D47A1)',
            color: 'white',
            '& .MuiListItemIcon-root': {
                color: 'white',
            },
            '& .Mui-selected': {
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.3)',
                },
            },
            '& .MuiListItemButton-root:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
            },
          },
        }}
      >
        {DrawerContent}
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          height: '100%',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
};

export default DashboardLayout;